import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Container } from '../components/ui/Container';
import { Section } from '../components/ui/Section';
import { SEO } from '../components/SEO';
import { User, Building2, ArrowRight, Shield, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function StartHere() {
  const {
    t
  } = useLanguage();
  return <Layout data-id="element-4828">
      <SEO title="Security Risk Assessment | CyberSecurityProtocol" description="Take a quick security risk assessment to receive a personalized cybersecurity protocol, prioritized action checklist, and practical recommendations." keywords="security risk assessment, cybersecurity assessment, personal security assessment, organizational security assessment, cyber risk evaluation" url="https://cybersecurityprotocol.org/start" data-id="element-4829" />

      <Section spacing="xl" data-id="element-4830">
        <Container size="lg" data-id="element-4831">
          {/* Header - Mobile Optimized */}
          <div className="text-center mb-12 sm:mb-16 animate-fade-in-up px-4" data-id="element-4832">
            <Badge variant="primary" size="lg" className="mb-4 sm:mb-6" data-id="element-4833">
              {t('start.badge')}
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-100 mb-4 sm:mb-6" data-id="element-4834">
              {t('start.title')}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-3 sm:mb-4" data-id="element-4835">
              {t('start.subtitle')}
            </p>
            <p className="text-xs sm:text-sm text-slate-500 max-w-2xl mx-auto italic" data-id="element-4836">
              {t('start.disclaimer')}
            </p>
          </div>

          {/* Assessment Cards - Mobile Optimized */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto mb-8 sm:mb-12" data-id="element-4837">
            {/* Personal Assessment */}
            <AssessmentCard icon={<User className="h-6 w-6 sm:h-8 sm:w-8" data-id="element-4839" />} badge={t('start.personal.badge')} title={t('start.personal.title')} description={t('start.personal.desc')} benefits={[t('start.personal.benefit1'), t('start.personal.benefit2'), t('start.personal.benefit3')]} audience={t('start.personal.audience')} ctaText={t('start.personal.cta')} ctaLink="/risk-assessment?type=personal" accentColor="cyan" delay="100" data-id="element-4838" />

            {/* Organization Assessment */}
            <AssessmentCard icon={<Building2 className="h-6 w-6 sm:h-8 sm:w-8" data-id="element-4841" />} badge={t('start.org.badge')} title={t('start.org.title')} description={t('start.org.desc')} benefits={[t('start.org.benefit1'), t('start.org.benefit2'), t('start.org.benefit3')]} audience={t('start.org.audience')} ctaText={t('start.org.cta')} ctaLink="/risk-assessment?type=organization" accentColor="emerald" delay="200" data-id="element-4840" />
          </div>

          {/* Trust Indicators - Mobile Optimized */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto animate-fade-in-up animation-delay-300 px-4" data-id="element-4842">
            <TrustIndicator icon={<Clock className="h-4 w-4 sm:h-5 sm:w-5" data-id="element-4844" />} text={t('start.trust.time')} data-id="element-4843" />
            <TrustIndicator icon={<Shield className="h-4 w-4 sm:h-5 sm:w-5" data-id="element-4846" />} text={t('start.trust.standards')} data-id="element-4845" />
            <TrustIndicator icon={<ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" data-id="element-4848" />} text={t('start.trust.results')} data-id="element-4847" />
          </div>
        </Container>
      </Section>
    </Layout>;
}
interface AssessmentCardProps {
  icon: React.ReactNode;
  badge: string;
  title: string;
  description: string;
  benefits: string[];
  audience: string;
  ctaText: string;
  ctaLink: string;
  accentColor: 'cyan' | 'emerald';
  delay: string;
}
function AssessmentCard({
  icon,
  badge,
  title,
  description,
  benefits,
  audience,
  ctaText,
  ctaLink,
  accentColor,
  delay
}: AssessmentCardProps) {
  const {
    t
  } = useLanguage();
  const colors = {
    cyan: {
      badge: 'primary',
      icon: 'text-cyan-400 bg-cyan-500/10',
      iconHover: 'group-hover:bg-cyan-500/20',
      border: 'group-hover:border-cyan-500/30',
      glow: 'group-hover:shadow-[0_0_30px_rgba(6,182,212,0.3)]'
    },
    emerald: {
      badge: 'success',
      icon: 'text-emerald-400 bg-emerald-500/10',
      iconHover: 'group-hover:bg-emerald-500/20',
      border: 'group-hover:border-emerald-500/30',
      glow: 'group-hover:shadow-[0_0_30px_rgba(16,185,129,0.3)]'
    }
  };
  const color = colors[accentColor];
  return <Card glass padding="lg" className={`group relative overflow-hidden ${color.glow} transition-all duration-300 animate-fade-in-up animation-delay-${delay} h-full`} data-id="element-4849">
      {/* Cyber Border */}
      <div className={`absolute inset-0 rounded-2xl border border-slate-800 ${color.border} transition-colors`} data-id="element-4850"></div>

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full" data-id="element-4851">
        {/* Icon & Badge - Mobile Optimized */}
        <div className="flex items-start justify-between mb-4 sm:mb-6" data-id="element-4852">
          <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-xl ${color.icon} ${color.iconHover} flex items-center justify-center transition-all duration-300`} data-id="element-4853">
            {icon}
          </div>
          <Badge variant={color.badge as any} size="sm" data-id="element-4854">
            {badge}
          </Badge>
        </div>

        {/* Title - Mobile Optimized */}
        <h3 className="text-lg sm:text-xl font-bold text-slate-100 mb-2 sm:mb-3" data-id="element-4855">
          {title}
        </h3>

        {/* Description - Mobile Optimized */}
        <p className="text-xs sm:text-sm text-slate-400 mb-4 sm:mb-6 leading-relaxed" data-id="element-4856">
          {description}
        </p>

        {/* Benefits - Mobile Optimized */}
        <div className="mb-4 sm:mb-6 flex-grow" data-id="element-4857">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2 sm:mb-3" data-id="element-4858">
            {t('start.youWillReceive')}
          </p>
          <ul className="space-y-1.5 sm:space-y-2" data-id="element-4859">
            {benefits.map((benefit, index) => <li key={index} className="flex items-start text-xs sm:text-sm text-slate-300" data-id="element-4860">
                <span className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-4861">
                  ▸
                </span>
                <span className="break-words" data-id="element-4862">{benefit}</span>
              </li>)}
          </ul>
        </div>

        {/* Audience - Mobile Optimized */}
        <p className="text-xs text-slate-500 italic mb-4 sm:mb-6" data-id="element-4863">{audience}</p>

        {/* CTA - Mobile Optimized */}
        <Link to={ctaLink} className="w-full" data-id="element-4864">
          <Button variant="outline" fullWidth className="group/btn" data-id="element-4865">
            <span className="text-sm" data-id="element-4866">{ctaText}</span>
            <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" data-id="element-4867" />
          </Button>
        </Link>
      </div>

      {/* Scanline Effect */}
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(6,182,212,0.02)_50%)] bg-[length:100%_2px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-4868"></div>
    </Card>;
}
function TrustIndicator({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="flex items-center justify-center space-x-2 text-slate-400" data-id="element-4869">
      <div className="text-cyan-400 flex-shrink-0" data-id="element-4870">{icon}</div>
      <span className="text-xs sm:text-sm font-medium text-center" data-id="element-4871">{text}</span>
    </div>;
}